import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Lead } from 'models/Leads';
import { Col } from 'react-bootstrap';
import { HasFeeItem, HasOrderItems } from 'models/hasOrderItems/HasOrderItems';
import { isLeadImported, showCleaningFeeRow } from 'utils/lead/leadUtils';
import { Quote, QuoteFeeType } from 'models/Quote';
import { useFormContext } from 'react-hook-form';
import TextField from 'components/domain/lead/modal/tabs/details/order/fields/common/TextField';
import { OrderViewTableHeader } from '../OrderView.styles';
import {
  OrderViewPriceDetailsListWrapper,
  PriceDetailsListTotal,
} from './OrderViewPriceDetails.styles';
import OrderViewPriceDetailsListItemTotal from './OrderViewPriceDetailsListItemTotal';
import Rent from './items/Rent';
import Taxes from './items/Taxes';
import Fees from './items/Fees';
import Services from './items/Services';
import SecurityDeposit from './items/SecurityDeposit';
import AdjustmentItems from './items/AdjustmentItems';
import AddOrderAdjustmentButton from './actions/AddOrderAdjustmentButton';
import { PriceDetailsItem } from './OrderViewPriceDetailsListItemRow';
import OrderViewTotalSubtitle from './OrderViewTotalSubtitle';

const OrderViewPriceDetailsInner = ({
  order,
  lead,
  handleOrderAdjustment,
  showTitle,
  allowEdit,
  isLoading,
  showAmountPaidRow,
  showBalanceRow,
  defaultQuote,
}: {
  order: HasOrderItems;
  lead: Lead;
  handleOrderAdjustment?: boolean;
  showTitle?: boolean;
  allowEdit?: boolean;
  isLoading?: boolean;
  showAmountPaidRow?: boolean;
  showBalanceRow?: boolean;
  defaultQuote: Quote | null;
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const [duplicateFeeIndex, setDuplicateFeeIndex] = useState<number | null>(
    null,
  );

  const {
    rent,
    uid: orderUid,
    includeSecurityDepositInTotal,
    securityDeposit,
    totalAmount,
    fees,
    services,
    adjustmentItems,
    currency,
    amountPaid,
  } = order || {};

  const balance = lead?.order?.balance ?? 0;
  const balanceDueLocalDate = lead?.order?.balanceDueLocalDate ?? null;
  const fullPaymentTiming = lead?.property?.pricing?.fullPaymentTiming ?? 0;

  const isImported = isLeadImported(lead);

  const cleanupAndSaveOverrides = (otherFees: HasFeeItem[]) => {
    const cleanOtherFees = otherFees?.filter(
      (fee) =>
        !(
          fee.feeId === null &&
          fee.removed &&
          !defaultQuote?.fees.otherFees.some((f) => f.name === fee.name)
        ),
    );

    setValue('overrides.fees.otherFees', cleanOtherFees);

    setValue(`quote.fees.otherFees`, cleanOtherFees, {
      shouldDirty: true,
    });
  };

  const onItemDeleted = (otherFees: HasFeeItem[], currentIndex: number) => {
    otherFees[currentIndex].removed = true;

    cleanupAndSaveOverrides(otherFees);
  };

  const onItemUpdated = (
    otherFees: HasFeeItem[],
    oldValue: string,
    newValue: string,
  ) => {
    const oldFeeIndex = otherFees?.findIndex(
      (fee) => fee.feeId === null && fee.name === oldValue,
    );
    otherFees[oldFeeIndex].removed = true;

    otherFees?.push({
      ...otherFees[oldFeeIndex],
      name: newValue,
      removed: false,
    });

    cleanupAndSaveOverrides(otherFees);

    return true;
  };

  const getPriceDetailsItemForFee = (
    otherFees: HasFeeItem[],
    fee: HasFeeItem,
    currentIndex: number,
  ): PriceDetailsItem => {
    const feeItem: PriceDetailsItem = {
      label:
        !fee.feeUid && allowEdit && fee.isEditable ? (
          <TextField
            key={`${fee.name}-fee-${fee.feeUid || currentIndex}`}
            value={fee.name}
            isEditable
            onItemUpdated={(_val: string, newVal: string) => {
              const isFeeAlreadyInQuote = otherFees?.find(
                (f: HasFeeItem) => f.name === newVal,
              );
              if (isFeeAlreadyInQuote) {
                setDuplicateFeeIndex(currentIndex);
                return false;
              }

              setDuplicateFeeIndex(null);
              return onItemUpdated([...otherFees], _val, newVal);
            }}
          />
        ) : (
          fee.name
        ),
      amount: fee.netPrice,
      currency,
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      onDelete:
        fee.feeId === null && fee.isEditable && allowEdit
          ? () => onItemDeleted([...otherFees], currentIndex)
          : null,
      isEditable: allowEdit && fee.isEditable,
      hasError: duplicateFeeIndex === currentIndex,
      onItemUpdated: (val) => {
        setValue(`quote.fees.otherFees[${currentIndex}].netPrice`, val, {
          shouldDirty: true,
        });
      },
    };

    return feeItem;
  };

  const getTaxFees = (otherFees: HasFeeItem[]): PriceDetailsItem[] => {
    return otherFees?.reduce(
      (acc: PriceDetailsItem[], fee: HasFeeItem, currentIndex) => {
        if (fee.removed || !fee.enabled) {
          return acc;
        }

        if (fee.type === QuoteFeeType.TAX) {
          const feeItem = getPriceDetailsItemForFee(
            otherFees,
            fee,
            currentIndex,
          );
          acc.push(feeItem);
        }

        if (fee.taxAmount > 0) {
          const feeTaxItem: PriceDetailsItem = {
            label: t('componentOrder.otherFeeTax', {
              name: fee.name,
              tax: fee.taxationRate,
            }),
            amount: fee.taxAmount,
            currency,
          };

          acc.push(feeTaxItem);
        }

        return acc;
      },
      [],
    );
  };

  const getFees = (otherFees: HasFeeItem[]): PriceDetailsItem[] => {
    return otherFees.reduce(
      (acc: PriceDetailsItem[], fee: HasFeeItem, currentIndex) => {
        if (fee.removed || !fee.enabled) {
          return acc;
        }

        if (fee.type === QuoteFeeType.TAX) {
          return acc;
        }

        const feeItem = getPriceDetailsItemForFee(otherFees, fee, currentIndex);
        acc.push(feeItem);

        return acc;
      },
      [],
    );
  };

  return (
    <div>
      <OrderViewTableHeader>
        {showTitle && (
          <Col xs={6}>
            <h4 data-testid="price-details-title">
              {t('pageOrder.priceDetails.sectionTitle')}
            </h4>
          </Col>
        )}
        <Col xs={showTitle ? 6 : 12} className="text-right">
          {!isImported && handleOrderAdjustment && (
            <AddOrderAdjustmentButton
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              orderUid={orderUid}
              leadUid={lead.uid}
              currency={currency}
            />
          )}
        </Col>
      </OrderViewTableHeader>

      <OrderViewPriceDetailsListWrapper
        $isLoading={isLoading}
        data-testid="price-details-wrapper"
        className="price-details-wrapper"
      >
        {!!rent && (
          <Rent
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            allowEdit={allowEdit}
            order={order}
            lead={lead}
            isImported={isImported}
          />
        )}
        {!isImported && (
          <Taxes
            // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            priceDetailsItems={getTaxFees(fees?.otherFees)}
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            allowEdit={allowEdit}
            order={order}
          />
        )}
        {!isImported &&
          (fees?.otherFees.find((o) => o.type !== QuoteFeeType.TAX) ||
            showCleaningFeeRow({
              lead,
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              cleaningFee: fees?.cleaningFee,
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              allowEdit,
            })) && (
            <Fees
              // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              priceDetailsItems={getFees(fees?.otherFees)}
              lead={lead}
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              allowEdit={allowEdit}
              order={order}
            />
          )}
        {/*
         // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
        {!isImported && services?.length > 0 && <Services order={order} />}
        {/*
         // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
        {!isImported && adjustmentItems?.length > 0 && (
          <AdjustmentItems
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            handleOrderAdjustment={handleOrderAdjustment}
            lead={lead}
            order={order}
          />
        )}

        {!isImported && includeSecurityDepositInTotal && !!securityDeposit && (
          <SecurityDeposit
            includeSecurityDepositInTotal={includeSecurityDepositInTotal}
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            allowEdit={allowEdit}
            order={order}
          />
        )}

        <PriceDetailsListTotal
          className="price-details-list-total"
          data-testid="price-details-list-total"
        >
          <OrderViewPriceDetailsListItemTotal
            currency={currency}
            data-testid="price-details-list-item-total-total"
            label={
              isImported
                ? t('componentOrder.payout')
                : t('componentOrder.total')
            }
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            subtitle={!showBalanceRow && <OrderViewTotalSubtitle lead={lead} />}
            amount={totalAmount}
          />

          {/*
           // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
          {showAmountPaidRow && amountPaid > 0 && !isImported && (
            <OrderViewPriceDetailsListItemTotal
              currency={currency}
              data-testid="price-details-list-item-total-amountPaid"
              label={t('componentOrder.amountPaid')}
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              amount={amountPaid}
            />
          )}

          {showBalanceRow && !isImported && balance > 0 && (
            <OrderViewPriceDetailsListItemTotal
              currency={currency}
              data-testid="price-details-list-item-total-balance"
              label={t('componentOrder.balanceDue')}
              amount={balance}
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              subtitle={
                balanceDueLocalDate &&
                t('componentOrder.balanceDueDate', {
                  date: format(parseISO(balanceDueLocalDate), 'MMM dd, yyyy'),
                  days: fullPaymentTiming,
                })
              }
            />
          )}
        </PriceDetailsListTotal>

        {!isImported &&
          !includeSecurityDepositInTotal &&
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          securityDeposit > 0 && (
            <SecurityDeposit
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              includeSecurityDepositInTotal={includeSecurityDepositInTotal}
              // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              allowEdit={allowEdit}
              order={order}
            />
          )}
      </OrderViewPriceDetailsListWrapper>
    </div>
  );
};

OrderViewPriceDetailsInner.defaultProps = {
  handleOrderAdjustment: true,
  showAmountPaidRow: true,
  showBalanceRow: true,
  showTitle: true,
  allowEdit: false,
  isLoading: false,
};

export default OrderViewPriceDetailsInner;
